import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';
import { getMaintenanceStatus, toggleMaintenance } from '../../../services/api';
import { toast } from 'react-toastify';

function MaintenanceAdmin({ userData }) {
    const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isToggling, setIsToggling] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const response = await getMaintenanceStatus();
                setIsMaintenanceMode(response.maintenance);
            } catch (error) {
                console.error('Failed to fetch maintenance status:', error);
                toast.error('Failed to load maintenance status');
            } finally {
                setIsLoading(false);
            }
        };

        fetchStatus();
    }, []);

    const handleToggleClick = () => {
        setShowConfirmModal(true);
    };

    const handleConfirmToggle = async () => {
        setShowConfirmModal(false);
        setIsToggling(true);

        try {
            const response = await toggleMaintenance();
            setIsMaintenanceMode(response.maintenance);
            toast.success(response.message, {
                autoClose: 3000,
            });
        } catch (error) {
            console.error('Failed to toggle maintenance:', error);
            toast.error('Failed to toggle maintenance mode', {
                autoClose: 3000,
            });
        } finally {
            setIsToggling(false);
        }
    };

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    return (
        <>
            {showConfirmModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => setShowConfirmModal(false)}
                    ></div>

                    <div
                        className="relative bg-offwhite border-2 border-gray-800 p-6 max-w-md w-full mx-4 z-[101]"
                        style={{
                            boxShadow: '0 4px 0 0 rgba(0, 0, 0, 0.5)'
                        }}
                    >
                        <h2 className="text-maroon uppercase font-['Jersey_10'] text-[20px] tracking-[1px] mb-4">
                            Confirm Action
                        </h2>

                        <hr className="border-gold mb-4" />

                        <p className="text-gray-800 font-['Jersey_10'] text-[17px] mb-6">
                            Are you sure you want to {isMaintenanceMode ? 'disable' : 'enable'} maintenance mode?
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 justify-between">
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-colors order-2 sm:order-1"
                                style={{
                                    boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmToggle}
                                className="bg-maroon hover:opacity-90 text-white px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] transition-opacity order-1 sm:order-2"
                                style={{
                                    boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)',
                                    backgroundColor: '#5A2929'
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <AdminLayout>
                <ContentBox theme="default" tint="red" header="Site Maintenance">
                    {isLoading ? (
                        <p>Loading...</p>
                    ) : (
                        <>
                            <div className="mb-4">
                                <p className="mb-2">Current Status:
                                    <span className={`ml-2 font-bold ${isMaintenanceMode ? 'text-red-600' : 'text-green-600'}`}>
                                        {isMaintenanceMode ? 'ACTIVE' : 'INACTIVE'}
                                    </span>
                                </p>
                            </div>

                            <button
                                onClick={handleToggleClick}
                                disabled={isToggling}
                                className={`px-6 py-2 font-['Jersey_10'] uppercase text-[17px] tracking-[1px] text-white transition-opacity ${
                                    isMaintenanceMode ? 'bg-green-600' : 'bg-red-600'
                                } ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
                                style={{
                                    boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                {isToggling ? 'Please wait...' : (isMaintenanceMode ? 'Disable Maintenance' : 'Enable Maintenance')}
                            </button>

                            <div className="mt-6">
                                <p className="font-bold mb-2">Information:</p>
                                <p className="text-sm">When maintenance mode is enabled, only admins can log in and access the site. Normal staff are locked out along with everyone else.</p>
                            </div>
                        </>
                    )}
                </ContentBox>
            </AdminLayout>
        </>
    );
}

export default MaintenanceAdmin;